import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { CheckCircle2, Edit2, Loader2, Send, Trash2, X, Filter } from 'lucide-react';
import { toast } from 'sonner';

const GIFT_TYPES = [
  { key: 'one_time', label: 'One-time' },
  { key: 'recurring', label: 'Recurring' },
  { key: 'in_kind', label: 'In-kind' },
  { key: 'pledge', label: 'Pledge' },
  { key: 'planned', label: 'Planned' },
];

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const ISO_DAYS_AGO = (n) => {
  const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10);
};

/**
 * Gifts tab with per-row checkboxes and a floating bulk-action bar.
 * Mass actions: acknowledge selected, acknowledge ALL unacknowledged, delete selected.
 * Filters: search box (donor/campaign/notes) + "unacknowledged only" toggle + quick "over 72h old" filter.
 */
export function GiftsTab({ api, gifts, onChange, setEditingGift, setGiftDialogOpen }) {
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState('');
  const [ackFilter, setAckFilter] = useState('all');   // 'all' | 'unacked' | 'overdue'
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const cutoff72 = ISO_DAYS_AGO(3);
    return gifts.filter(g => {
      if (ackFilter === 'unacked' && g.acknowledged) return false;
      if (ackFilter === 'overdue' && (g.acknowledged || (g.date || '') > cutoff72)) return false;
      if (!q) return true;
      const s = q.toLowerCase();
      return (g.donor_name || '').toLowerCase().includes(s)
          || (g.campaign || '').toLowerCase().includes(s)
          || (g.notes || '').toLowerCase().includes(s);
    });
  }, [gifts, q, ackFilter]);

  const unackedCount = gifts.filter(g => !g.acknowledged).length;
  const selectedUnackedIds = useMemo(
    () => filtered.filter(g => selected.has(g.id) && !g.acknowledged).map(g => g.id),
    [filtered, selected]
  );

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAllVisible = () => setSelected(prev => {
    const allChecked = filtered.length > 0 && filtered.every(g => prev.has(g.id));
    return new Set(allChecked ? [] : filtered.map(g => g.id));
  });
  const allChecked = filtered.length > 0 && filtered.every(g => selected.has(g.id));

  const ackSelected = async () => {
    if (selectedUnackedIds.length === 0) { toast.error('No unacknowledged gifts selected'); return; }
    setBusy(true);
    try {
      const res = await api.post('/donor-gift-bulk-acknowledge', { gift_ids: selectedUnackedIds });
      toast.success(`Acknowledged ${res.data.acknowledged} gift${res.data.acknowledged === 1 ? '' : 's'}`);
      setSelected(new Set());
      onChange();
    } catch { toast.error('Bulk acknowledge failed'); }
    finally { setBusy(false); }
  };

  const ackAllUnacked = async () => {
    if (unackedCount === 0) { toast.info('Nothing to acknowledge — you\u2019re caught up.'); return; }
    if (!window.confirm(`Mark all ${unackedCount} unacknowledged gift${unackedCount === 1 ? '' : 's'} as acknowledged? This action will stamp today\u2019s date on each.`)) return;
    setBusy(true);
    try {
      const res = await api.post('/donor-gift-bulk-acknowledge', { all_unacknowledged: true });
      toast.success(`Acknowledged ${res.data.acknowledged} gift${res.data.acknowledged === 1 ? '' : 's'}`);
      setSelected(new Set());
      onChange();
    } catch { toast.error('Bulk acknowledge failed'); }
    finally { setBusy(false); }
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} gift${selected.size === 1 ? '' : 's'}? Donor totals will recompute.`)) return;
    setBusy(true);
    try {
      const res = await api.delete('/donor-gifts-bulk', { data: { gift_ids: [...selected] } });
      toast.success(`Deleted ${res.data.deleted}`);
      setSelected(new Set());
      onChange();
    } catch { toast.error('Bulk delete failed'); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="gifts-tab">
      {/* Filter/search + top-level 'ack all unacked' */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Input placeholder="Search donor, campaign, notes…" value={q}
               onChange={e => setQ(e.target.value)} className="max-w-sm" data-testid="gifts-search" />
        <div className="flex items-center gap-1 border rounded px-1 py-0.5 text-xs" data-testid="gifts-ack-filter">
          <Filter className="h-3 w-3 text-gray-400 ml-1" />
          {['all', 'unacked', 'overdue'].map(f => (
            <button key={f}
                    onClick={() => setAckFilter(f)}
                    className={`px-2 py-1 rounded ${ackFilter === f ? 'bg-[#6B3FA0] text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                    data-testid={`gifts-filter-${f}`}>
              {f === 'all' ? 'All' : f === 'unacked' ? `Unacknowledged (${unackedCount})` : 'Overdue (72h+)'}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {unackedCount > 0 && (
            <Button size="sm" onClick={ackAllUnacked} disabled={busy}
                    className="bg-[#6B3FA0] hover:bg-[#5A3489]" data-testid="ack-all-unacked-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
              Acknowledge all {unackedCount}
            </Button>
          )}
        </div>
      </div>

      {/* Bulk action bar (only when a selection exists) */}
      {selected.size > 0 && (
        <div className="sticky top-14 z-20 bg-white border-2 border-[#6B3FA0] rounded-lg shadow p-3 mb-3 flex items-center gap-2 flex-wrap"
             data-testid="gifts-bulk-bar">
          <span className="text-sm font-semibold text-[#6B3FA0]">{selected.size} selected</span>
          <Button size="sm" onClick={ackSelected}
                  disabled={busy || selectedUnackedIds.length === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="gifts-bulk-ack-btn">
            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
            Acknowledge {selectedUnackedIds.length > 0 ? `(${selectedUnackedIds.length})` : ''}
          </Button>
          <Button size="sm" variant="outline" onClick={deleteSelected} disabled={busy}
                  className="text-red-600 border-red-300 hover:bg-red-50" data-testid="gifts-bulk-delete-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="gifts-bulk-clear">
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Table header (checkbox select-all) */}
      {filtered.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-1.5 border-b text-xs text-gray-500 mb-1">
          <input type="checkbox" checked={allChecked} onChange={toggleAllVisible} data-testid="gifts-select-all" />
          <span>{filtered.length} of {gifts.length} shown</span>
        </div>
      )}

      {/* Gift rows */}
      <div className="grid gap-1.5">
        {filtered.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-gray-500">
            {gifts.length === 0 ? 'No gifts logged yet.' : 'No gifts match the current filter.'}
          </CardContent></Card>
        ) : filtered.map(g => {
          const isOverdue = !g.acknowledged && (g.date || '') <= ISO_DAYS_AGO(3);
          return (
            <Card key={g.id} className={selected.has(g.id) ? 'ring-2 ring-[#6B3FA0]' : ''}
                  data-testid={`gift-row-${g.id}`}>
              <CardContent className="p-3 flex items-center gap-3 flex-wrap">
                <input type="checkbox" checked={selected.has(g.id)} onChange={() => toggle(g.id)}
                       className="shrink-0 cursor-pointer" data-testid={`gift-select-${g.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{g.donor_name}</span>
                    <span className="text-sm text-gray-500">· {g.date}</span>
                    {g.campaign && <Badge variant="outline" className="text-xs">{g.campaign}</Badge>}
                    <Badge variant="outline" className="text-xs">{GIFT_TYPES.find(t => t.key === g.gift_type)?.label || g.gift_type}</Badge>
                    {g.acknowledged
                      ? <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]" title={g.acknowledged_at ? `Sent ${g.acknowledged_at.slice(0, 10)}` : ''}>
                          <CheckCircle2 className="h-3 w-3 mr-0.5" /> Acknowledged
                        </Badge>
                      : isOverdue
                        ? <Badge className="bg-red-100 text-red-800 border-0 text-[10px]">Overdue (72h+)</Badge>
                        : <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">Pending ack</Badge>}
                  </div>
                  {g.notes && <div className="text-xs text-gray-500 mt-0.5 truncate">{g.notes}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-lg">{fmt$(g.amount)}</span>
                  <Button size="sm" variant="ghost" onClick={() => { setEditingGift(g); setGiftDialogOpen(true); }}
                          data-testid={`edit-gift-${g.id}`}>
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
